import { booleanAttribute } from "#ce/utils/props";
import { systemColors } from "#ce/composables/color";

export const COLORS = [...systemColors, "error", "custom"];

export const props = {
  /**
   * Visual variant forwarded to children when `connectOnSm` is enabled. Required when `connectOnSm` is true so the connected container picks up the right surface tokens.
   *
   * @type {String}
   * @default "default"
   * @values default, neutral, inverted
   * @storybook-control radio
   */
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "neutral", "inverted"].includes(value),
  },

  /**
   * Fixed number of columns per row on desktop. Set to "auto" for a responsive auto-fill grid.
   *
   * @type {Number|String}
   * @default "auto"
   * @values auto, 1, 2, 3, 4
   * @storybook-control radio
   */
  itemsPerRow: {
    type: [Number, String],
    default: "auto",
    validator: (value) => ["auto", "1", "2", "3", "4"].includes(String(value)),
  },

  /**
   * Collapse children into a single connected container on small screens with shared background and dividers.
   *
   * @type {Boolean|String}
   * @default false
   */
  connectOnSm: booleanAttribute(),

  /**
   * Equalise card heights to the tallest child on both desktop and small screens. Ignored when `connectOnSm` is true.
   *
   * @type {Boolean|String}
   * @default false
   */
  equalizeHeights: booleanAttribute(),

  /**
   * Small-screen override for equalised heights. Leave undefined to inherit `equalizeHeights`.
   *
   * @type {Boolean|String}
   * @default undefined
   * @storybook-table-subcategory Small Screen
   */
  equalizeHeightsSm: booleanAttribute(undefined),

  /**
   * Minimum height applied to every card on desktop (e.g. "25rem" or "400px").
   *
   * @type {String|Number}
   * @default 0
   */
  minHeight: {
    type: [String, Number],
    default: 0,
  },

  /**
   * Minimum height applied to every card on small screens (e.g. "16rem" or "240px").
   *
   * @type {String|Number}
   * @default 0
   */
  minHeightSm: {
    type: [String, Number],
    default: 0,
  },

  /**
   * Theme colour for the inverted variant. Used as the connected-container background and divider colour when `connectOnSm` is enabled.
   *
   * @type {String}
   * @default "primary"
   * @storybook-control select
   */
  color: {
    type: String,
    default: "primary",
    validator: (value) => COLORS.includes(value),
  },
};
